import React, { Fragment, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  DropdownProps,
  List,
  Radio,
  SearchBox,
  StatusTip,
} from 'tea-component';
import { connect } from 'umi';
import { NO_DATE } from '@/pages/opinion/constants';
import BubbleCommon, { TipsMapType } from '@op/components/BubbleCommon';
import { TOptionsItem, IMultipleCommonType } from '../../types';
import {
  multipleMapStateToProps,
  moreKeyMultipleDispatchToProps,
} from '../../connect/multiple';
import useIntls from '@opm/hooks/useIntls';
import './index.scss';

export type RadioAllMultipleSelectType = {
  options: TOptionsItem[];
  itemRender?: (item: TOptionsItem, isEn: boolean) => string | JSX.Element;
  valueKeys: string;
  title: string;
  titleCn: string;
  filterClassName?: string;
  allRadioTips?: TipsMapType;
  leftText?: string;
  allText?: string;
  allTextCn?: string;
  hasSelectAllBtn?: boolean;
  allValueConfig?: string;
} & Omit<DropdownProps, 'button' | 'children'>;

export function RadioAllMultipleSelect({
  leftText = NO_DATE.EMPTY,
  allText = 'All',
  allTextCn = '全部',
  value = [],
  change,
  allRadioTips,
  options,
  itemRender,
  overlayClassName,
  boxClassName,
  hasSelectAllBtn = true,
  title,
  titleCn,
  ...otherDropdownProps
}: RadioAllMultipleSelectType & IMultipleCommonType) {
  const { intl, isEn } = useIntls();
  const [keyword, setKeyword] = useState('');
  const isSelected = useMemo(() => {
    return value.length > 0;
  }, [value]);

  const renderOptionItem = (item: TOptionsItem) => {
    if (itemRender) {
      return itemRender(item, isEn);
    }
    return isEn ? item.text : item.textCn;
  };

  const renderOptions = useMemo(() => {
    if (!options) return [];

    if (keyword) {
      const lowerKeyword = keyword.toLowerCase();
      return options.filter((item: TOptionsItem) => {
        return (
          item.text.toLowerCase().includes(lowerKeyword) ||
          item.textCn.toLowerCase().includes(lowerKeyword)
        );
      });
    }

    return options;
  }, [keyword, options]);

  function buttonElement() {
    const selectOptions: TOptionsItem[] = options?.filter((item) =>
      value.includes(item.value),
    );

    let text: any = selectOptions.length;

    if (selectOptions.length < 3) {
      const list = selectOptions.slice(0, 2);
      text = (
        <>
          {list.map((item, index) => {
            return (
              <Fragment key={index}>
                {renderOptionItem(item)} {list.length - 1 === index ? '' : ','}
              </Fragment>
            );
          })}
        </>
      );
    }

    if (selectOptions.length === 0) {
      text = isEn ? allText : allTextCn;
    }

    return (
      <div className="dbom-dropdow-button">
        <label className="dbom-dropdow-button-label">
          {isEn ? title : titleCn}
        </label>
        <div className="dbom-dropdow-button-value">{text}</div>
      </div>
    );
  }

  return (
    <div className="dbom-radio-all-multiple-select-wrapper">
      <Dropdown
        placement="bottom-start"
        overlayClassName={`dbom-all-select-overlay ${overlayClassName}`}
        boxClassName={`dbom-all-select-box ${boxClassName}`}
        clickClose={false}
        appearance="button"
        onClose={() => {}}
        button={buttonElement()}
        {...otherDropdownProps}
      >
        <SearchBox
          className="dbom-search-input-info"
          value={keyword}
          onChange={(value) => setKeyword(value)}
          onSearch={(value) => setKeyword(value)}
          onClear={() => setKeyword('')}
          placeholder={intl({ id: 'Search' })}
        />
        {renderOptions.length === 0 ? (
          <StatusTip
            status={'empty'}
            emptyText={intl('No data')}
            className="dbom-search-input-no-data"
          />
        ) : (
          <>
            <div className="radio-all-box">
              <Radio
                name="all"
                className="radio-item"
                onClick={() => change([])}
                value={value.length === 0}
              >
                {isEn ? allText : allTextCn}
                {allRadioTips && (
                  <BubbleCommon
                    placement="top"
                    tipsMap={allRadioTips}
                  ></BubbleCommon>
                )}
              </Radio>
            </div>
            <div className="title-box">
              <label className="label-title">{intl({ id: leftText })}</label>
              {hasSelectAllBtn && (
                <Button
                  onClick={() => {
                    if (isSelected) {
                      change([]);
                    } else {
                      change?.(options?.map((item) => item.value));
                    }
                  }}
                  type="link"
                >
                  {intl({
                    id: isSelected ? 'Clear All' : 'Select All',
                  })}
                </Button>
              )}
            </div>
            <div className="dbom-list-box">
              <Checkbox.Group
                className="checkbox-group-box"
                value={value}
                onChange={change}
              >
                <List type="option" className="select-list-box">
                  {renderOptions.map((item: TOptionsItem) => {
                    return (
                      <List.Item key={item.value} className="select-list-item">
                        <Checkbox className="check-box-item" name={item.value}>
                          {renderOptionItem(item)}
                        </Checkbox>
                      </List.Item>
                    );
                  })}
                </List>
              </Checkbox.Group>
            </div>
          </>
        )}
      </Dropdown>
    </div>
  );
}

export default connect(
  multipleMapStateToProps,
  moreKeyMultipleDispatchToProps,
)(RadioAllMultipleSelect);
